import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  IconButton,
  Grid,
  Tooltip,
  CircularProgress,
  Modal,
  Button,
  Chip,
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import api, { API_BASE_URL } from "../../../utils/api";
import ImageAnnotatorCanvas from "./ImageAnnotatorCanvas";

const RequestFilesViewer = ({ requestId, onPreviewImage }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [annotateFile, setAnnotateFile] = useState(null);
  const [annotatedFileIds, setAnnotatedFileIds] = useState(new Set());

  const buildFileUrl = (filePath) => {
    const filename = (filePath || "").split("/").pop();
    return `${API_BASE_URL}/request-images/${encodeURIComponent(filename)}`;
  };

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/request-files/${requestId}`);
        setFiles(res.data || []);
      } catch (err) {
        console.error("Failed to fetch request files", err);
        setFiles([]);
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchFiles();
    }
  }, [requestId]);

  // Load annotation status for all image files
  useEffect(() => {
    if (!files.length || !requestId) return;
    const imageFiles = files.filter(f => f.mime_type?.startsWith("image/") || /\.(png|jpg|jpeg|gif|webp)$/i.test(f.file_name || ""));
    Promise.all(
      imageFiles.map(f =>
        api.get(`/annotations/${requestId}/${f.id}`)
          .then(res => res.data?.objects?.length > 0 ? f.id : null)
          .catch(() => null)
      )
    ).then(results => {
      setAnnotatedFileIds(new Set(results.filter(Boolean)));
    });
  }, [files, requestId]);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const modalPaperSx = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(960px, 92vw)",
    maxHeight: "90vh",
    bgcolor: "background.paper",
    borderRadius: "18px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
    border: "1px solid rgba(47,99,173,0.08)",
    overflow: "hidden",
  };

  const getFileTypeLabel = (file) => {
    const type = String(file.file_type || "").toLowerCase();

    if (type.startsWith("image/")) return "Image";
    if (type === "application/pdf") return "PDF";
    if (
      type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      file.file_name?.toLowerCase().endsWith(".docx")
    ) {
      return "DOCX";
    }
    if (type === "text/plain") return "Text";
    return "File";
  };

  const getIcon = (type) => {
    if (type?.startsWith("image/")) {
      return <ImageIcon sx={{ color: "#2f63ad" }} />;
    }
    if (type === "application/pdf") {
      return <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />;
    }
    if (
      type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return <DescriptionIcon sx={{ color: "#2f63ad" }} />;
    }
    return <InsertDriveFileIcon sx={{ color: "#6b778c" }} />;
  };

  const handleDelete = async (file) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    try {
      await api.delete(`/request-files/${file.id}`);
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
    } catch (err) {
      console.error("Failed to delete file:", err);
    }
  };

  const handleDownload = (file) => {
    const url = buildFileUrl(file.file_url);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", file.file_name);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!files.length) {
    return (
      <Card sx={{ ...pageCardSx, p: 3, textAlign: "center", mt: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No uploaded files
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
          No files have been uploaded for this request yet.
        </Typography>
      </Card>
    );
  }

  return (
    <Box mt={3}>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <AttachFileIcon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Uploaded Files
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Review, preview, annotate, download, or remove files attached to this request.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 6 } }}>
          <Grid container spacing={2.25}>
          {files.map((file) => {
  const isImage = file.file_type?.startsWith("image/");
  const fileUrl = buildFileUrl(file.file_url);

  return (
    <Grid item xs={12} sm={6} xl={4} key={file.id}>
      <Card
        sx={{
          borderRadius: "16px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          p: 2,
          backgroundColor: "#fff",
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1.25,
              mb: 1.25,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                minWidth: 0,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(47,99,173,0.08)",
                  flexShrink: 0,
                }}
              >
                {getIcon(file.file_type)}
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: "#223142",
                    lineHeight: 1.25,
                  }}
                  noWrap
                  title={file.file_name}
                >
                  {file.file_name}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "#6b778c", mt: 0.25 }}
                >
                  {file.file_type || "Unknown type"}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display:"flex", gap:0.75, flexWrap:"wrap" }}>
              <Chip
                label={getFileTypeLabel(file)}
                size="small"
                variant="outlined"
                sx={{ borderRadius:"999px", fontWeight:700, flexShrink:0 }}
              />
              {annotatedFileIds.has(file.id) && (
                <Chip
                  label="Annotated"
                  size="small"
                  sx={{ borderRadius:"999px", fontWeight:700, flexShrink:0,
                    backgroundColor:"rgba(37,99,235,0.1)", color:"#2563eb",
                    border:"1px solid rgba(37,99,235,0.3)" }}
                />
              )}
            </Box>
          </Box>

          {isImage && (
            <Box
              sx={{
                mt: 1,
                width: "100%",
                height: 180,
                borderRadius: "14px",
                overflow: "hidden",
                border: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(47,99,173,0.03)",
                cursor: "pointer",
              }}
              onClick={() => onPreviewImage?.(fileUrl, file.file_name)}
            >
              <Box
                component="img"
                src={fileUrl}
                alt={file.file_name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 0.5,
            flexWrap: "wrap",
            mt: 1.25,
          }}
        >
          {isImage ? (
            <Tooltip title="Preview">
              <IconButton
                onClick={() => onPreviewImage?.(fileUrl, file.file_name)}
                aria-label="Preview image"
                sx={{
                  border: "1px solid rgba(47,99,173,0.12)",
                  borderRadius: "10px",
                  backgroundColor: "rgba(47,99,173,0.03)",
                }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Preview">
              <IconButton
                onClick={() => setPreviewFile(file)}
                aria-label="Preview file"
                sx={{
                  border: "1px solid rgba(47,99,173,0.12)",
                  borderRadius: "10px",
                  backgroundColor: "rgba(47,99,173,0.03)",
                }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Download">
            <IconButton
              onClick={() => handleDownload(file)}
              aria-label="Download file"
              sx={{
                border: "1px solid rgba(47,99,173,0.12)",
                borderRadius: "10px",
                backgroundColor: "rgba(47,99,173,0.03)",
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          {isImage && (
            <Tooltip title="Annotate">
              <IconButton
                onClick={() => setAnnotateFile(file)}
                aria-label="Annotate"
                sx={{
                  border: "1px solid rgba(47,99,173,0.12)",
                  borderRadius: "10px",
                  backgroundColor: "rgba(47,99,173,0.03)",
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Delete">
            <IconButton
              aria-label="Delete file"
              onClick={() => handleDelete(file)}
              sx={{
                border: "1px solid rgba(220,38,38,0.12)",
                borderRadius: "10px",
                color: "#dc2626",
                backgroundColor: "rgba(220,38,38,0.04)",
                "&:hover": {
                  backgroundColor: "rgba(220,38,38,0.1)",
                },
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Card>
    </Grid>
  );
})}
          </Grid>
        </Box>
      </Card>

      <Modal
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        aria-labelledby="preview-title"
        aria-describedby="preview-body"
      >
        <Box sx={modalPaperSx}>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Typography
              variant="h6"
              id="preview-title"
              sx={{
                fontWeight: 700,
                color: "#223142",
                minWidth: 0,
                wordBreak: "break-word",
              }}
            >
              {previewFile?.file_name}
            </Typography>

            <IconButton onClick={() => setPreviewFile(null)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, overflow: "auto", maxHeight: "calc(90vh - 72px)" }}>
            {previewFile?.file_type === "application/pdf" ? (
              <iframe
                src={buildFileUrl(previewFile.file_url)}
                title="PDF Preview"
                width="100%"
                height="600px"
                style={{
                  border: "1px solid rgba(47,99,173,0.08)",
                  borderRadius: "12px",
                  background: "#fff",
                }}
              />
            ) : previewFile?.file_type === "text/plain" ? (
              <iframe
                src={buildFileUrl(previewFile.file_url)}
                title="Text Preview"
                width="100%"
                height="600px"
                style={{
                  border: "1px solid rgba(47,99,173,0.08)",
                  borderRadius: "12px",
                  backgroundColor: "#f5f5f5",
                }}
              />
            ) : previewFile?.file_type ===
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
              previewFile?.file_name?.toLowerCase().endsWith(".docx") ? (
              <Box>
                <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
                  DOCX preview is not supported directly. Download the file to view it.
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(previewFile)}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Download File
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Cannot preview this file type. Please download it to view.
              </Typography>
            )}
          </Box>
        </Box>
      </Modal>

      <Modal
        open={Boolean(annotateFile)}
        onClose={() => setAnnotateFile(null)}
        aria-labelledby="annotate-title"
        disableEnforceFocus
        disableAutoFocus
      >
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 2000,
          }}
        >
          {annotateFile && (
            <ImageAnnotatorCanvas
              file={annotateFile}
              requestId={requestId}
              onClose={() => {
                // Refresh annotated badge after annotator closes
                api.get(`/annotations/${requestId}/${annotateFile.id}`)
                  .then(res => {
                    if (res.data?.objects?.length > 0)
                      setAnnotatedFileIds(prev => new Set([...prev, annotateFile.id]));
                  }).catch(()=>{});
                setAnnotateFile(null);
              }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default RequestFilesViewer;